import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Copy, ExternalLink, Check, X, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const AdminPaymentLinks = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    amount: '',
    currency: 'TRY',
    description: '',
    expires_in_hours: 24,
  });

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await axios.get(`${API}/payment-links`);
      setLinks(response.data);
    } catch (error) {
      console.error('Error fetching links:', error);
      toast.error('Linkler yüklenirken hata oluştu');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/payment-links`, {
        ...formData,
        amount: parseFloat(formData.amount),
      });
      toast.success('Ödeme linki oluşturuldu!');
      fetchLinks();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error creating link:', error);
      toast.error('Link oluşturulurken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (linkId) => {
    const paymentUrl = `${window.location.origin}/pay/${linkId}`;
    navigator.clipboard.writeText(paymentUrl);
    toast.success('Link kopyalandı!');
  };

  const markAsPaid = async (linkId) => {
    if (!window.confirm('Bu ödemeyi tahsil edildi olarak işaretlemek istediğinize emin misiniz?')) return;

    try {
      await axios.patch(`${API}/payment-links/${linkId}/mark-paid`, {
        method: 'manual',
        marked_by: 'admin',
        marked_at: new Date().toISOString()
      });
      toast.success('Ödeme tahsil edildi olarak işaretlendi!');
      fetchLinks();
    } catch (error) {
      console.error('Error marking as paid:', error);
      toast.error('Hata oluştu');
    }
  };

  const deleteLink = async (linkId) => {
    if (!window.confirm('Bu ödeme linkini silmek istediğinizden emin misiniz?')) return;

    try {
      await axios.delete(`${API}/payment-links/${linkId}`);
      toast.success('Link silindi!');
      fetchLinks();
    } catch (error) {
      console.error('Error deleting link:', error);
      toast.error('Hata oluştu');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_email: '',
      amount: '',
      currency: 'TRY',
      description: '',
      expires_in_hours: 24,
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'paid':
        return 'bg-green-100 text-green-700';
      case 'expired':
        return 'bg-gray-100 text-gray-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'paid':
        return <Check className="w-4 h-4" />;
      case 'expired':
        return <X className="w-4 h-4" />;
      case 'cancelled':
        return <X className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Bekliyor';
      case 'paid':
        return 'Ödendi';
      case 'expired':
        return 'Süresi Doldu';
      case 'cancelled':
        return 'İptal';
      default:
        return status;
    }
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 
              className="text-3xl font-bold text-gray-900"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              data-testid="payment-links-title"
            >
              Ödeme Linkleri
            </h1>
            <p className="text-gray-600 mt-1">Müşterilere göndermek için ödeme linkleri oluşturun</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-payment-link-btn">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Link Oluştur
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Yeni Ödeme Linki Oluştur</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="customer_name">Müşteri Adı *</Label>
                    <Input
                      id="customer_name"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      required
                      data-testid="customer-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="customer_email">Müşteri E-posta *</Label>
                    <Input
                      id="customer_email"
                      name="customer_email"
                      type="email"
                      value={formData.customer_email}
                      onChange={handleChange}
                      required
                      data-testid="customer-email"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="amount">Tutar (TL) *</Label>
                    <Input
                      id="amount"
                      name="amount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={formData.amount}
                      onChange={handleChange}
                      required
                      data-testid="amount"
                    />
                  </div>
                  <div>
                    <Label htmlFor="expires_in_hours">Geçerlilik Süresi (Saat) *</Label>
                    <Input
                      id="expires_in_hours"
                      name="expires_in_hours"
                      type="number"
                      min="1"
                      max="168"
                      value={formData.expires_in_hours}
                      onChange={handleChange}
                      required
                      data-testid="expires-hours"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="description">Açıklama *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={3}
                    placeholder="Örn: Web sitesi geliştirme projesi ön ödeme"
                    data-testid="description"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full" data-testid="submit-link">
                  {loading ? 'Oluşturuluyor...' : 'Link Oluştur'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-4">
          {links.map((link) => (
            <div key={link.id} className="bg-white rounded-xl border border-gray-200 p-6" data-testid={`link-card-${link.id}`}>
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-gray-900">{link.customer_name}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(link.status)}`}>
                      {getStatusIcon(link.status)}
                      {getStatusLabel(link.status)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">{link.customer_email}</p>
                  <p className="text-gray-700 mb-2">{link.description}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="font-semibold text-lg text-gray-900">{link.amount.toLocaleString('tr-TR')} {link.currency}</span>
                    <span>•</span>
                    <span>Oluşturulma: {format(new Date(link.created_at), 'dd MMM yyyy HH:mm', { locale: tr })}</span>
                    <span>•</span>
                    <span className={link.status === 'pending' && new Date(link.expires_at) < new Date() ? 'text-red-600 font-medium' : ''}>
                      Son Geçerlilik: {format(new Date(link.expires_at), 'dd MMM yyyy HH:mm', { locale: tr })}
                    </span>
                  </div>
                  {link.paid_at && (
                    <div className="mt-2 text-sm text-green-600">
                      Ödenme: {format(new Date(link.paid_at), 'dd MMM yyyy HH:mm', { locale: tr })}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3 mb-3">
                <code className="flex-1 text-sm text-gray-700 overflow-x-auto">
                  {window.location.origin}/pay/{link.link_id}
                </code>
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(link.link_id)} data-testid={`copy-${link.id}`}>
                  <Copy className="w-4 h-4 mr-1" />
                  Kopyala
                </Button>
                <a href={`/pay/${link.link_id}`} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline" data-testid={`open-${link.id}`}>
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Aç
                  </Button>
                </a>
              </div>

              <div className="flex gap-2">
                {link.status === 'pending' && (
                  <Button size="sm" onClick={() => markAsPaid(link.link_id)} data-testid={`mark-paid-${link.id}`}>
                    <Check className="w-4 h-4 mr-1" />
                    Ödendi Olarak İşaretle
                  </Button>
                )}
                <Button size="sm" variant="destructive" onClick={() => deleteLink(link.link_id)} data-testid={`delete-${link.id}`}>
                  <X className="w-4 h-4 mr-1" />
                  Sil
                </Button>
              </div>
            </div>
          ))}

          {links.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Henüz ödeme linki oluşturulmamış.
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPaymentLinks;
